import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useDialogFocus, TrappedPanel } from "../../hooks/useDialogFocus";
import {
  X,
  Calendar,
  Check,
  Plus,
  Trash2,
  Archive,
  ArchiveRestore,
  Link,
  ChevronLeft,
  ChevronRight,
  CalendarClock,
  CalendarRange,
  MoveRight,
  FileUp,
  CornerDownRight,
  Briefcase,
} from "lucide-react";
import {
  Task,
  SubTask,
  List,
  Tag,
  NewTask,
  CalendarEvent,
} from "../../types";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { useAddTaskLink } from "../../hooks/mutations/useAddLink";
import { useDeleteTaskLink } from "../../hooks/mutations/useDeleteLink";
import { useUpdateTaskLink } from "../../hooks/mutations/useUpdateLink";
import { useUploadTaskAttachment } from "../../hooks/mutations/useUploadAttachment";
import { useDeleteTaskAttachment } from "../../hooks/mutations/useDeleteAttachment";
import { useRenameTaskAttachment } from "../../hooks/mutations/useRenameAttachment";
import { useSetSubtaskTags } from "../../hooks/mutations/useSetSubtaskTags";
import { useCreateList } from "../../hooks/mutations/useCreateList";
import { parseDateFromTitle, splitGraphemes } from "../../lib/utils";
import { formatShortcut } from "../../lib/platform";
import { isValidWorkHoursSlot } from "../../lib/workingHours";
import { registerPendingWrite, clearPendingWrite } from "../../lib/undoStack";
import {
  getCurrentAccessToken,
  keepaliveSupabaseRequest,
} from "../../lib/keepaliveRest";
import { buildTaskUpdatePayload, sameTagIds, taskUpdateDiff } from "../../lib/taskUpdatePayload";

import RichTextEditor from "../forms/LazyRichTextEditor";
import { TagPopover } from "./TagPopover";
import AddListDialog from "./AddListDialog";
import { LinkList } from "../forms/LinkList";
import AnimatePresence from "../ui/AnimatePresence";
import TimePicker from "../forms/TimePicker";
import ScheduledDateField from "../forms/ScheduledDateField";
import WorkingHoursToggle from "../forms/WorkingHoursToggle";
import DurationPicker from "./task/DurationPicker";
import { linksAfterAddModeDelete } from "./task/addModeLinkDelete";
import LinkForm from "./task/LinkForm";
import ListPicker from "./task/ListPicker";
import TaskPriorityButtons from "./task/TaskPriorityButtons";
import { useSharedListSync } from "./task/useSharedListSync";
import RecurrenceConfig from "./task/RecurrenceConfig";
import TagPicker from "./task/TagPicker";
import SubtaskDetail from "./task/SubtaskDetail";
import SubtaskList from "./task/SubtaskList";
import TaskAttachmentsSection from "./task/TaskAttachmentsSection";
import TaskPickerDialog from "./TaskPickerDialog";
import CompleteWithSubtasksDialog from "./CompleteWithSubtasksDialog";
import SchedulePickerCalendar from "./LazySchedulePickerCalendar";
import Tooltip from "../ui/Tooltip";
import { FullWindowLayer } from "../ui/FullWindowLayer";
import { DEFAULT_LIST_EMOJI } from "../../constants/defaults";

/**
 * The scheduling cluster: fields edited by *clicking* rather than typing —
 * the Schedule row, the Repeat toggle, and every recurrence control.
 *
 * Each save is one database write **and** one Google Calendar sync, and these
 * fields get edited by clicks spaced seconds apart, so they take a longer
 * auto-save window than the 500ms that already coalesces typing perfectly
 * well. Local state still updates instantly — only the write waits.
 *
 * `working_hours_only` is deliberately absent: it's a lone toggle rather than
 * a cluster of clicks, so the short window costs it nothing.
 */
const SCHEDULING_FIELDS: readonly (keyof Task)[] = [
  "scheduled_date",
  "scheduled_time",
  "duration",
  "recurrence_frequency",
  "recurrence_interval",
  "recurrence_day_of_week",
  "recurrence_end_date",
  "persistent",
];
const SCHEDULING_FIELD_SET: ReadonlySet<string> = new Set(SCHEDULING_FIELDS);

export const SAVE_DEBOUNCE_MS = 500;
export const SCHEDULING_SAVE_DEBOUNCE_MS = 2000;

/**
 * Pick the auto-save window for one change, or `null` when saving would write
 * nothing and no save is owed.
 *
 * The long window applies only when the edit lands *entirely* inside the
 * scheduling cluster. Mixing in a typed field (title, description) or a
 * sub-task edit drops the whole batch back to the short window rather than
 * leaving that edit unwritten for two seconds — most-recent-change wins.
 */
function autoSaveDelayFor(
  prev: Task | NewTask,
  next: Task | NewTask,
): number | null {
  const { changed, changedFields } = taskUpdateDiff(
    prev as Partial<Task>,
    next as Partial<Task>,
  );
  if (!changed) return null;
  const schedulingOnly =
    changedFields.length > 0 &&
    changedFields.every((field) => SCHEDULING_FIELD_SET.has(field));
  return schedulingOnly ? SCHEDULING_SAVE_DEBOUNCE_MS : SAVE_DEBOUNCE_MS;
}

interface BaseTaskDialogProps {
  lists: List[];
  tags: Tag[];
  onClose: () => void;
  userId?: string;
  // True when this mount is the result of prev/next task navigation
  // (dialog was already open). Swaps the dramatic slide-up entrance for
  // a subtle content fade so the panel doesn't visibly re-scale.
  isNavigating?: boolean;
  // True when another dialog (e.g. the keyboard-shortcuts help or search) is
  // open on top of this one. While that's the case the task dialog yields its
  // global keyboard shortcuts — Escape and prev/next navigation — to the
  // dialog above, so Escape dismisses the top dialog instead of closing the
  // task underneath it.
  hasOverlayAbove?: boolean;
  // Schedule picker context: when these are passed, the "Pick on calendar"
  // affordance appears next to the date inputs (desktop only).
  allTasksForPicker?: Task[];
  calendarEventsForPicker?: CalendarEvent[];
  onFetchCalendarEvents?: (start: string, end: string) => Promise<void>;
  isLoadingCalendarEvents?: boolean;
  weekStartDay?: number | null;
}

interface AddTaskDialogProps extends BaseTaskDialogProps {
  mode: "add";
  task: NewTask;
  onTaskChange: (task: NewTask) => void;
  onSave: () => void;
}

interface EditTaskDialogProps extends BaseTaskDialogProps {
  mode: "edit";
  task: Task;
  onTaskChange: (task: Task) => void;
  onSave: () => void;
  selectedTagIds: string[];
  onTagsChange: (tagIds: string[]) => void;
  allTasks?: Task[];
  /** Arrow visibility. Falls back to allTasks when omitted. */
  canNavigate?: boolean;
  allTasksForMove?: Task[];
  onNavigateNext?: () => void;
  onNavigatePrev?: () => void;
  onDelete?: (taskId: string) => void;
  // Deletes the sub-task currently open in the breadcrumb sub-view. The header
  // trash icon routes here (instead of onDelete) whenever a sub-task is active,
  // so it always acts on the item being viewed rather than the parent task.
  onDeleteSubtask?: (subtaskId: string) => void;
  onArchive?: (taskId: string) => void;
  // Routes completion through the global toggleTask path so the undo toast
  // appears and the dialog closes — mirrors what list-view checkboxes already
  // do. When `completeIncompleteSubtasksFirst` is true, the parent finishes
  // any open subtasks before flipping the parent.
  onToggleComplete?: (
    taskId: string,
    opts?: { completeIncompleteSubtasksFirst?: boolean },
  ) => Promise<void> | void;
  onMoveSubtask?: (subtaskId: string, targetTaskId: string) => void;
  onConvertSubtaskToTask?: (subtaskId: string) => void;
  onConvertToSubtask?: (taskId: string) => void;
  activeSubtaskId?: string | null;
  onActiveSubtaskChange?: (subtaskId: string | null) => void;
}

type TaskDialogProps = AddTaskDialogProps | EditTaskDialogProps;

const TaskDialog: React.FC<TaskDialogProps> = (props) => {
  const {
    mode,
    task,
    onTaskChange: emitTaskChange,
    lists,
    tags,
    onClose,
    onSave,
    userId,
    isNavigating = false,
    hasOverlayAbove = false,
    allTasksForPicker,
    calendarEventsForPicker,
    onFetchCalendarEvents,
    isLoadingCalendarEvents,
    weekStartDay,
  } = props;

  const panelRef = useRef<HTMLDivElement>(null);
  useDialogFocus(panelRef, mode === "edit" ? "task-dialog-edit" : "task-dialog-add");
  const { defaultTaskDuration } = useUserPreferences();

  // Set only from a control in this dialog. A new `task` prop that arrives
  // without it is a server copy, and must not be written back.
  const userEditedRef = useRef(false);
  const onTaskChange = (next: Task | NewTask) => {
    userEditedRef.current = true;
    if (mode === "edit") emitTaskChange(next as Task);
    else emitTaskChange(next as NewTask);
  };

  // Link mutations are called here rather than threaded down as props — the
  // hooks return callables with exactly the signatures the old
  // onAddLink/onDeleteLink props carried. Each is still gated on edit mode
  // below: add mode keeps its links in the draft NewTask until the task
  // exists. The sub-task counterparts moved into SubtaskDetail, which is the
  // only thing that ever called them.
  const addTaskLink = useAddTaskLink();
  const deleteTaskLink = useDeleteTaskLink();
  const updateTaskLink = useUpdateTaskLink();

  // Attachment writes. Add mode has no task id, so each call stays gated below.
  const uploadTaskAttachment = useUploadTaskAttachment();
  const deleteTaskAttachment = useDeleteTaskAttachment();
  const renameTaskAttachment = useRenameTaskAttachment();

  // Subtask tag writes, likewise called here rather than threaded down — the
  // hook returns a callable with exactly the signature the old
  // onSetSubtaskTags prop carried. The one call site is already gated on edit
  // mode: add mode holds subtask tags in the draft NewTask until the subtask
  // exists. Note this is the *persisted* write only — the task-level tag
  // selection stays on `selectedTagIds`/`onTagsChange` because that is the
  // parent's editing buffer, not cache-derived state (roadmap #39).
  const setSubtaskTags = useSetSubtaskTags();

  // List creation, likewise called here rather than threaded down as the old
  // onAddList prop. The inline "new list" form is available in both modes —
  // add mode needs the list to exist before it can point the draft NewTask at
  // it — so unlike the mutations above this one is deliberately ungated.
  const createList = useCreateList();

  // Shared lists (#286): committing a list change, the two move
  // confirmations, disabling the picker per ADR-0003, and "Added by" — all
  // in one hook so this file doesn't grow past its grandfathered ceiling.
  // writeTask flattens onTaskChange's discriminated-union signature into a
  // plain one — TS narrows mode/onTaskChange together here, inline, but
  // can't carry that narrowing across the hook's own function boundary.
  const writeTask = (t: Task | NewTask) => {
    if (mode === "edit") onTaskChange(t as Task);
    else onTaskChange(t as NewTask);
  };
  const sharedListSync = useSharedListSync({
    mode,
    task,
    viewerId: userId,
    lists,
    onTaskChange: writeTask,
  });

  const pickerAvailable =
    !!allTasksForPicker && !!onFetchCalendarEvents;
  const [pickerTarget, setPickerTarget] = useState<
    "task" | "subtask" | null
  >(null);

  // Capture the navigation flag at mount time. The parent toggles isNavigating
  // back to false right after the route settles (editingTask syncs from the
  // URL in a follow-up render), which would otherwise swap the className from
  // animate-content-fade back to animate-dialog-panel and replay the full
  // slide-up entrance — causing the "small and big" flicker.
  const [mountedIsNavigating] = useState(isNavigating);

  const [localActiveSubtaskId, setLocalActiveSubtaskId] = useState<
    string | null
  >(null);

  const activeSubtaskId =
    props.mode === "edit" && props.activeSubtaskId !== undefined
      ? props.activeSubtaskId
      : localActiveSubtaskId;

  const setActiveSubtaskId = (id: string | null) => {
    if (props.mode === "edit" && props.onActiveSubtaskChange) {
      props.onActiveSubtaskChange(id);
    } else {
      setLocalActiveSubtaskId(id);
    }
  };
  // Mirrored for the document-level keydown listener below, which needs the
  // current closure without re-registering every render (this function is
  // rebuilt on each one). Assigned in a layout effect rather than during
  // render: a render React starts and then abandons must not leave the ref
  // pointing at state that never committed. Layout effects run before passive
  // ones, so the listener registered below still sees the fresh value.
  const setActiveSubtaskIdRef = useRef(setActiveSubtaskId);
  useLayoutEffect(() => {
    setActiveSubtaskIdRef.current = setActiveSubtaskId;
  });
  const [showTagPopover, setShowTagPopover] = useState(false);
  const [tagPopoverAnchor, setTagPopoverAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [showListPopover, setShowListPopover] = useState(false);
  const [showAddListDialog, setShowAddListDialog] = useState(false);
  // Shown when the user tries to enable Working hours only but the current
  // scheduled slot falls outside Mon–Fri 9–5. The dialog blocks the toggle
  // until the slot is fixed; we never silently mutate the user's data.
  const [showWorkHoursBlock, setShowWorkHoursBlock] = useState<
    "task" | "subtask" | null
  >(null);
  const [hoveringCompleteCheckbox, setHoveringCompleteCheckbox] =
    useState(false);
  const [hoveringSubtaskCompleteCheckbox, setHoveringSubtaskCompleteCheckbox] =
    useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListEmoji, setNewListEmoji] = useState(DEFAULT_LIST_EMOJI);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<
    string | null
  >(null);
  const [isDescriptionEditing, setIsDescriptionEditing] = useState(
    mode === "add",
  );
  const [showMoveSubtaskPicker, setShowMoveSubtaskPicker] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);

  // Scheduling-cluster writes go through the same `onTaskChange` path as every
  // other field; the auto-save effect below simply gives them a longer debounce
  // (see SCHEDULING_FIELDS). Kept as a named helper because the recurrence
  // controls each write several fields at once — picking a frequency also
  // re-anchors the date, for instance.
  const updateScheduling = (updates: Partial<Task>) => {
    if (mode === "edit") onTaskChange({ ...task, ...updates } as Task);
    else onTaskChange({ ...task, ...updates } as NewTask);
  };

  // Get the active subtask object
  const activeSubtask =
    mode === "edit" && activeSubtaskId
      ? (task as Task).subtasks?.find((st) => st.id === activeSubtaskId) || null
      : null;

  // Edit mode specific state
  const isFirstRender = useRef(true);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  // Mirror hasOverlayAbove in a ref: the document-level keydown listener
  // captures it and must see the live value (a dialog opening on top after
  // mount) without re-registering on every change.
  const hasOverlayAboveRef = useRef(hasOverlayAbove);
  hasOverlayAboveRef.current = hasOverlayAbove;

  // Page teardown — Cmd+W, reload, following a link out of the app — kills the
  // debounce timer without running any React cleanup, so the write has to be
  // handed to the global pagehide flush the moment it is scheduled. Same
  // machinery deferred task deletes use; `keepaliveSupabaseRequest` is what
  // lets the request outlive the page.
  //
  // Two limits worth knowing: the flush writes the task row but cannot run the
  // Google Calendar sync (an edge-function call is not safe during teardown —
  // the next edit syncs it), and it does not reconcile sub-task adds/deletes.
  // Neither survived a tab close before this either.
  const pendingWriteKey =
    mode === "edit" ? `task-autosave-${(task as Task).id}` : "";

  // Captured once per dialog open: reading the session is async, and on
  // pagehide there is no room to await anything.
  const accessTokenRef = useRef<string | null>(null);
  useEffect(() => {
    if (mode !== "edit") return;
    let cancelled = false;
    void getCurrentAccessToken().then((token) => {
      if (!cancelled) accessTokenRef.current = token;
    });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const taskRef = useRef(task);
  taskRef.current = task;

  const registerUnloadFlush = useCallback(
    (timer: ReturnType<typeof setTimeout>) => {
      if (!pendingWriteKey) return;
      registerPendingWrite(pendingWriteKey, {
        timer,
        flush: () => {
          const accessToken = accessTokenRef.current;
          if (!accessToken) return;
          const current = taskRef.current as Task;
          keepaliveSupabaseRequest(
            `tasks?id=eq.${encodeURIComponent(current.id)}`,
            {
              method: "PATCH",
              body: {
                ...buildTaskUpdatePayload(current),
                updated_at: new Date().toISOString(),
              },
            },
            accessToken,
          );
        },
      });
    },
    [pendingWriteKey],
  );

  // Commit the debounced auto-save immediately instead of waiting out the
  // window. Every route out of the dialog calls this — Escape, the X, the
  // backdrop, prev/next (button and ⌥A/⌥D), archive, delete, both converts,
  // move-subtask, complete — plus the unmount effect below for the routes the
  // dialog can't see (browser back, in-app navigation). Page teardown is the
  // one exit React never gets to run for; `registerUnloadFlush` covers it.
  // Resolves to whether the write landed, so the destructive follow-ups (both
  // converts, skip-occurrence) can stand down if it never did. Never rejects,
  // nor does the debounce timer below: nearly every caller floats the promise,
  // which would turn an already-handled failure into an unhandled rejection.
  const flushPendingSave = async (): Promise<boolean> => {
    if (mode !== "edit" || !saveTimeoutRef.current) return true;
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = null;
    clearPendingWrite(pendingWriteKey);
    return Promise.resolve(onSaveRef.current()).then(() => true, () => false);
  };
  // The keydown listener needs the current flush without re-registering on
  // every keystroke. Layout-effect assignment so a ref left behind by an
  // abandoned render can't flush against state the app never rendered.
  const flushPendingSaveRef = useRef(flushPendingSave);
  useLayoutEffect(() => {
    flushPendingSaveRef.current = flushPendingSave;
  });

  // Prev/next only exist in edit mode. Mirroring them keeps the keydown
  // listener off `props`, which changes identity on every render. Held as two
  // refs rather than one `{ next, prev }` object so the mirror doesn't
  // allocate on every render for a value read only inside a key handler.
  const navigateNextRef = useRef<(() => void) | undefined>(undefined);
  const navigatePrevRef = useRef<(() => void) | undefined>(undefined);
  useLayoutEffect(() => {
    navigateNextRef.current =
      props.mode === "edit" ? props.onNavigateNext : undefined;
    navigatePrevRef.current =
      props.mode === "edit" ? props.onNavigatePrev : undefined;
  });

  // Hoisted so the effect dependency can be checked statically. Add mode
  // keeps this `undefined`, a stable value, so the effect does not re-fire.
  const autoSaveTagIds = props.mode === "edit" ? props.selectedTagIds : undefined;

  // Previous task snapshot, used only to classify what changed so the effect
  // below can pick a debounce window. Written during the effect (not render)
  // so an abandoned render can't advance the baseline past a save that never
  // got scheduled.
  const prevTaskRef = useRef<Task | NewTask>(task);
  const prevTagIdsRef = useRef(autoSaveTagIds);

  useLayoutEffect(() => {
    const prevTask = prevTaskRef.current;
    const prevTagIds = prevTagIdsRef.current;
    prevTaskRef.current = task;
    prevTagIdsRef.current = autoSaveTagIds;
    const editedByUser = userEditedRef.current;
    userEditedRef.current = false;

    if (mode === "edit") {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }

      const delay = autoSaveDelayFor(prevTask, task);
      // A server copy is not a click. A timer armed by a real edit stays.
      if (!editedByUser && sameTagIds(prevTagIds, autoSaveTagIds)) return;
      if (delay === null && sameTagIds(prevTagIds, autoSaveTagIds)) return;

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // No cleanup returned: clearing on a dependency change is already done
      // above, and clearing on unmount would race the unmount flush below —
      // `saveTimeoutRef` being non-null is exactly the "still owed a save"
      // signal that flush reads.
      const timer = setTimeout(() => {
        saveTimeoutRef.current = null;
        clearPendingWrite(pendingWriteKey);
        void Promise.resolve(onSaveRef.current()).catch(() => {});
      }, delay ?? SAVE_DEBOUNCE_MS);
      saveTimeoutRef.current = timer;
      registerUnloadFlush(timer);
    }
  }, [task, mode, autoSaveTagIds, pendingWriteKey, registerUnloadFlush]);

  // Leaving the dialog without going through one of the explicit exit routes —
  // the browser back button, or any in-app navigation that changes the route —
  // unmounts us with the timer still armed. React's cleanup above only clears
  // it, so without this the edit would be dropped. Runs on unmount only, and
  // is a no-op when an explicit route already flushed (that clears the timer).
  useEffect(
    () => () => {
      void flushPendingSaveRef.current();
    },
    [],
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // A dialog is layered on top of us (the keyboard-shortcuts help, search,
      // …). Let it own the keyboard — otherwise Escape would close this task
      // underneath instead of the dialog the user is actually looking at, and
      // prev/next would move tasks behind the open dialog.
      if (hasOverlayAboveRef.current) return;
      if (e.key === "Escape") {
        // An inline popover/dialog is layered on top of this task dialog (tag
        // picker, list picker, add-list). Each closes itself on Escape via its
        // own handler — bail out here so we don't also close the whole task
        // dialog underneath and discard the user's in-progress task.
        if (showAddListDialog || showTagPopover || showListPopover) return;
        if (pickerTarget) {
          setPickerTarget(null);
          return;
        }
        if (activeSubtaskId) {
          // Leaving the sub-view unmounts SubtaskDetail, which is what resets
          // its description-editing and link-form drafts.
          setActiveSubtaskIdRef.current(null);
          return;
        }
        if (mode === "edit") flushPendingSaveRef.current();
        onClose();
      }

      if (mode === "edit") {
        if (e.altKey && !e.metaKey && !e.ctrlKey && e.code === "KeyD") {
          e.preventDefault();
          flushPendingSaveRef.current();
          navigateNextRef.current?.();
        } else if (e.altKey && !e.metaKey && !e.ctrlKey && e.code === "KeyA") {
          e.preventDefault();
          flushPendingSaveRef.current();
          navigatePrevRef.current?.();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    onClose,
    mode,
    activeSubtaskId,
    pickerTarget,
    showAddListDialog,
    showTagPopover,
    showListPopover,
  ]);

  const selectedTagIds =
    mode === "edit" && props.mode === "edit"
      ? props.selectedTagIds
      : (task as NewTask).tag_ids || [];

  const handleTitleChange = (title: string) => {
    if (mode === "add") {
      const parsed = parseDateFromTitle(title);
      onTaskChange({
        ...task,
        title: parsed.title,
        scheduled_date: parsed.date || task.scheduled_date,
        scheduled_time: parsed.time || task.scheduled_time,
      } as NewTask);
    } else {
      onTaskChange({ ...task, title } as Task);
    }
  };

  // Keep-on-Calendar and Recurrence are mutually exclusive, and so are Working
  // hours only and Recurrence (Carry over and Working hours only combine
  // freely — that pairing is the whole point of the work-hours flag for
  // persistent tasks). The two flags below drive both halves of that: the
  // sections they gate are hidden while recurrence is on, and `RecurrenceConfig`
  // takes them to disable its own toggle while either is active.
  const persistentActive =
    mode === "edit"
      ? (task as Task).persistent
      : !!(task as NewTask).persistent;
  const workHoursActive =
    mode === "edit"
      ? !!(task as Task).working_hours_only
      : !!(task as NewTask).working_hours_only;

  const toggleCompletion = () => {
    if (mode !== "edit") return;
    const editTask = task as Task;
    const isTransitioningToComplete = !editTask.completed;
    const hasIncompleteSubtasks = !!editTask.subtasks?.some((s) => !s.completed);
    if (isTransitioningToComplete && hasIncompleteSubtasks) {
      setShowCompleteConfirm(true);
      return;
    }
    // Completing: route through the parent so the undo toast shows and the
    // dialog closes. Uncompleting stays on the in-place flow — no toast,
    // dialog open — to match the list view's behavior.
    if (
      isTransitioningToComplete &&
      props.mode === "edit" &&
      props.onToggleComplete
    ) {
      flushPendingSave();
      void props.onToggleComplete(editTask.id);
      return;
    }
    onTaskChange({
      ...editTask,
      completed: !editTask.completed,
    } as Task);
  };

  const confirmCompleteWithSubtasks = () => {
    if (mode !== "edit") return;
    const editTask = task as Task;
    setShowCompleteConfirm(false);
    if (props.mode === "edit" && props.onToggleComplete) {
      flushPendingSave();
      void props.onToggleComplete(editTask.id, {
        completeIncompleteSubtasksFirst: true,
      });
      return;
    }
    onTaskChange({
      ...editTask,
      completed: true,
      subtasks: editTask.subtasks?.map((s) =>
        s.completed ? s : { ...s, completed: true },
      ),
    } as Task);
  };

  // Helper to update a field on the active subtask
  const updateActiveSubtask = (updates: Partial<SubTask>) => {
    if (mode !== "edit" || !activeSubtaskId) return;
    const editTask = task as Task;
    onTaskChange({
      ...editTask,
      subtasks: editTask.subtasks?.map((st) =>
        st.id === activeSubtaskId ? { ...st, ...updates } : st,
      ),
    } as Task);
  };

  const toggleActiveSubtaskCompletion = () => {
    if (!activeSubtask) return;
    updateActiveSubtask({ completed: !activeSubtask.completed });
  };

  // Tag toggle for subtask view
  const toggleSubtaskTag = (tagId: string) => {
    if (mode !== "edit" || !activeSubtask || props.mode !== "edit") return;
    const currentTagIds = activeSubtask.tags?.map((t) => t.id) || [];
    const isSelected = currentTagIds.includes(tagId);
    const newTagIds = isSelected
      ? currentTagIds.filter((id) => id !== tagId)
      : [...currentTagIds, tagId];

    // Update local state for immediate UI feedback
    const editTask = task as Task;
    if (editTask.subtasks) {
      const updatedTags = props.tags.filter((t) => newTagIds.includes(t.id));
      onTaskChange({
        ...editTask,
        subtasks: editTask.subtasks.map((st) =>
          st.id === activeSubtaskId ? { ...st, tags: updatedTags } : st,
        ),
      } as Task);
    }

    // Persist to backend
    setSubtaskTags(activeSubtaskId!, newTagIds);
  };

  const toggleTag = (tagId: string) => {
    if (mode === "edit" && props.mode === "edit") {
      const isSelected = props.selectedTagIds.includes(tagId);
      const newTagIds = isSelected
        ? props.selectedTagIds.filter((id) => id !== tagId)
        : [...props.selectedTagIds, tagId];
      props.onTagsChange(newTagIds);
    } else if (mode === "add" && props.mode === "add") {
      const addTask = task as NewTask;
      const currentTagIds = addTask.tag_ids || [];
      const isSelected = currentTagIds.includes(tagId);
      const newTagIds = isSelected
        ? currentTagIds.filter((id) => id !== tagId)
        : [...currentTagIds, tagId];
      props.onTaskChange({ ...addTask, tag_ids: newTagIds });
    }
  };

  // Both the task and sub-task surfaces open the same tag popover, so the
  // trigger only reports where to anchor it.
  const toggleTagPopover = (anchor: HTMLElement) => {
    setTagPopoverAnchor(anchor);
    setShowTagPopover((v) => !v);
  };

  const handleAddList = async () => {
    if (!newListName.trim()) return;
    let list: List | null = null;
    try {
      list = await createList.mutateAsync({
        name: newListName,
        emoji: newListEmoji,
      });
    } catch {
      // useCreateList already reports the failure (Sentry + toast). Swallow the
      // throw so the form still resets and closes, matching the previous
      // addListToDb adapter, which resolved null on error rather than throwing.
    }
    if (list) {
      if (mode === "edit") {
        onTaskChange({
          ...task,
          list_id: list.id,
          list: list.name,
          listEmoji: list.emoji,
        } as Task);
      } else {
        onTaskChange({
          ...task,
          list_id: list.id,
        } as NewTask);
      }
    }
    setNewListName("");
    setNewListEmoji(DEFAULT_LIST_EMOJI);
    setShowAddListDialog(false);
  };

  // The link the form is currently editing, if any — it seeds LinkForm's
  // fields. In edit mode `editingLinkId` is the row's id; in add mode it is the
  // link's index in the draft, stringified.
  const editingLink = (() => {
    if (!editingLinkId) return undefined;
    if (mode === "edit") {
      return (task as Task).links?.find((l) => l.id === editingLinkId);
    }
    const index = parseInt(editingLinkId, 10);
    return isNaN(index) ? undefined : (task as NewTask).links?.[index];
  })();

  const handleAddLink = async (
    normalizedUrl: string,
    description: string,
  ) => {
    if (mode === "edit" && props.mode === "edit") {
      const editTask = task as Task;
      if (editingLinkId) {
        // Patch the row in place — see useUpdateTaskLink for why.
        const success = await updateTaskLink(editingLinkId, {
          url: normalizedUrl,
          description,
        });
        if (success) {
          onTaskChange({
            ...editTask,
            links: (editTask.links || []).map((l) =>
              l.id === editingLinkId
                ? { ...l, url: normalizedUrl, description }
                : l,
            ),
          } as Task);
        }
      } else {
        // Add new link
        const link = await addTaskLink(
          editTask.id,
          normalizedUrl,
          description,
        );
        if (link) {
          onTaskChange({
            ...editTask,
            links: [...(editTask.links || []), link],
          } as Task);
        }
      }
    } else if (mode === "add") {
      // For add mode, store links temporarily in the NewTask object
      const addTask = task as NewTask;
      if (editingLinkId) {
        // Edit existing link in add mode
        const index = parseInt(editingLinkId, 10);
        if (!isNaN(index)) {
          const updatedLinks = [...(addTask.links || [])];
          updatedLinks[index] = {
            url: normalizedUrl,
            description,
          };
          onTaskChange({
            ...addTask,
            links: updatedLinks,
          } as NewTask);
        }
      } else {
        // Add new link in add mode
        onTaskChange({
          ...addTask,
          links: [
            ...(addTask.links || []),
            { url: normalizedUrl, description },
          ],
        } as NewTask);
      }
    }

    setEditingLinkId(null);
    setShowLinkForm(false);
  };

  const handleEditLink = (linkId: string) => {
    if (mode === "edit" && props.mode === "edit") {
      const editTask = task as Task;
      if (editTask.links?.some((l) => l.id === linkId)) {
        setEditingLinkId(linkId);
        setShowLinkForm(true);
      }
    } else if (mode === "add") {
      // For add mode, linkId is the temporary index (String(i))
      const addTask = task as NewTask;
      const index = parseInt(linkId, 10);
      if (!isNaN(index) && addTask.links?.[index]) {
        setEditingLinkId(linkId);
        setShowLinkForm(true);
      }
    }
  };

  const handleDeleteLink = async (linkId: string | number) => {
    if (mode === "edit" && props.mode === "edit") {
      await deleteTaskLink(linkId as string);
      const editTask = task as Task;
      onTaskChange({
        ...editTask,
        links: editTask.links?.filter((l) => l.id !== linkId),
      } as Task);
    } else if (mode === "add") {
      const addTask = task as NewTask;
      const links = linksAfterAddModeDelete(addTask.links, linkId);
      if (links === addTask.links) return;
      onTaskChange({ ...addTask, links } as NewTask);
    }
  };

  const handleUploadAttachment = async (file: File) => {
    if (mode === "edit" && props.mode === "edit") {
      const editTask = task as Task;
      const attachment = await uploadTaskAttachment(editTask.id, file);
      if (attachment) {
        onTaskChange({
          ...editTask,
          attachments: [...(editTask.attachments || []), attachment],
        } as Task);
      }
    } else if (mode === "add") {
      // No task id yet to upload against — stage the file locally and let
      // useCreateTask upload it (via pending_files) once the task is saved.
      const addTask = task as NewTask;
      onTaskChange({
        ...addTask,
        pending_files: [...(addTask.pending_files || []), file],
      } as NewTask);
    }
  };

  const handleRemovePendingAttachment = (index: number) => {
    if (mode !== "add") return;
    const addTask = task as NewTask;
    onTaskChange({
      ...addTask,
      pending_files: (addTask.pending_files || []).filter(
        (_, i) => i !== index,
      ),
    } as NewTask);
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (mode === "edit" && props.mode === "edit") {
      setDeletingAttachmentId(attachmentId);
      const success = await deleteTaskAttachment(attachmentId);
      if (success) {
        const editTask = task as Task;
        onTaskChange({
          ...editTask,
          attachments: editTask.attachments?.filter(
            (a) => a.id !== attachmentId,
          ),
        } as Task);
      }
      setDeletingAttachmentId(null);
    }
  };

  // Rename persists via the DB hook, then patches the local editing snapshot so
  // the new name shows immediately — the snapshot isn't re-synced on cache
  // refetch (see useTaskEditing), so without this the rename only appears after
  // a reload. Mirrors handleDeleteAttachment.
  const handleRenameAttachment = async (
    attachmentId: string,
    newName: string,
  ): Promise<boolean> => {
    if (mode !== "edit" || props.mode !== "edit") {
      return false;
    }
    const success = await renameTaskAttachment(attachmentId, newName);
    if (success) {
      const editTask = task as Task;
      const trimmed = newName.trim();
      onTaskChange({
        ...editTask,
        attachments: editTask.attachments?.map((a) =>
          a.id === attachmentId ? { ...a, file_name: trimmed } : a,
        ),
      } as Task);
    }
    return success;
  };

  const hasMultipleTasks =
    mode === "edit" &&
    props.mode === "edit" &&
    (props.canNavigate ??
      ((props.allTasks?.length || 0) > 1 &&
        !!props.allTasks?.some((t) => t.id === (task as Task).id)));

  // Schedule picker apply / clear — rewrite the relevant scheduling fields on
  // the task or active subtask. These do NOT persist directly; persistence
  // happens when the user saves the task dialog (same path as typing in the
  // date input).
  const applySchedulePick = (
    target: "task" | "subtask",
    date: string,
    time: string | null,
    duration: number | null,
  ) => {
    if (target === "subtask") {
      updateActiveSubtask({
        scheduled_date: date,
        scheduled_time: time,
        duration,
      });
    } else if (mode === "edit") {
      onTaskChange({
        ...(task as Task),
        scheduled_date: date,
        scheduled_time: time,
        duration,
      });
    } else {
      onTaskChange({
        ...(task as NewTask),
        scheduled_date: date,
        scheduled_time: time,
        duration,
      });
    }
    setPickerTarget(null);
  };

  const clearSchedulePick = (target: "task" | "subtask") => {
    if (target === "subtask") {
      updateActiveSubtask({
        scheduled_date: null,
        scheduled_time: null,
        duration: null,
      });
    } else if (mode === "edit") {
      onTaskChange({
        ...(task as Task),
        scheduled_date: null,
        scheduled_time: null,
        duration: null,
        // Also clear schedule-dependent fields, mirroring the date-input clear.
        persistent: false,
        recurrence_frequency: null,
        recurrence_interval: 1,
        recurrence_day_of_week: null,
        recurrence_end_date: null,
      });
    } else {
      onTaskChange({
        ...(task as NewTask),
        scheduled_date: null,
        scheduled_time: null,
        duration: null,
      });
    }
    setPickerTarget(null);
  };

  // Resolve the target for the picker (task or active subtask) and the
  // corresponding initial values. Computed on each render so the picker
  // re-mounts with fresh values whenever it's opened.
  const pickerInitial = (() => {
    if (pickerTarget === "subtask" && activeSubtask) {
      return {
        targetKey: `subtask-${activeSubtask.id}`,
        targetTitle: activeSubtask.title,
        targetPriority: activeSubtask.priority,
        initialDate: activeSubtask.scheduled_date,
        initialTime: activeSubtask.scheduled_time,
        initialDuration: activeSubtask.duration,
        restrictToWorkingHours: !!activeSubtask.working_hours_only,
      };
    }
    if (pickerTarget === "task") {
      const taskAny = task as Task | NewTask;
      const id = mode === "edit" ? (task as Task).id : "__new_task__";
      return {
        targetKey: `task-${id}`,
        targetTitle: taskAny.title,
        targetPriority: taskAny.priority,
        initialDate: taskAny.scheduled_date,
        initialTime: taskAny.scheduled_time,
        initialDuration: taskAny.duration,
        restrictToWorkingHours: workHoursActive,
      };
    }
    return null;
  })();

  // Time picker + duration controls — shared between the Schedule section
  // (when recurrence is off) and the Recurrence section (when recurrence is on).
  // Caller decides when to render: Schedule gates on `scheduled_date`,
  // Recurrence gates on `scheduled_time` (the All-day pill controls whether
  // time is set).
  const renderTimeAndDurationFields = () => (
    <>
      <TimePicker
        value={task.scheduled_time || null}
        onChange={(newTime) => {
          const cleared = newTime ? {} : { duration: null };
          updateScheduling({ scheduled_time: newTime, ...cleared });
        }}
        restrictToWorkingHours={workHoursActive}
      />
      {task.scheduled_time && (
        <DurationPicker
          value={task.duration ?? null}
          onChange={(duration) => updateScheduling({ duration })}
        />
      )}
    </>
  );

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-30 p-2 sm:p-6 ${mountedIsNavigating ? "" : "animate-dialog-backdrop"}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (mode === "edit") flushPendingSave();
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={mode === "add" ? "Add Task" : "Edit Task"}
        className={`bg-warm-bg-card rounded-card border border-warm-border w-full max-w-7xl h-[95vh] sm:h-[85vh] flex flex-col shadow-soft-lg ${mountedIsNavigating ? "animate-content-fade" : "animate-dialog-panel"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-3 sm:px-6 py-3 sm:py-4 border-b border-warm-border flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            {activeSubtask ? (
              <div className="flex items-center gap-2 min-w-0">
                <div className="flex items-center gap-1 min-w-0 group/breadcrumb">
                  <Tooltip content={task.title} onlyWhenTruncated multiline>
                    <button
                      onClick={() => setActiveSubtaskId(null)}
                      className="font-heading text-sm text-warm-text-secondary hover:text-warm-blue dark:hover:text-warm-blue transition-colors truncate max-w-[100px] sm:max-w-[200px]"
                    >
                      {task.title}
                    </button>
                  </Tooltip>
                  {props.mode === "edit" && props.onMoveSubtask && (
                    <Tooltip content="Move to another task">
                      <button
                        onClick={() => setShowMoveSubtaskPicker(true)}
                        className="text-warm-text-tertiary hover:text-warm-blue p-0.5 rounded flex-shrink-0"
                      >
                        <MoveRight size={14} />
                      </button>
                    </Tooltip>
                  )}
                  {props.mode === "edit" && props.onConvertSubtaskToTask && (
                    <Tooltip content="Convert to task">
                      <button
                        onClick={async () => {
                          // Wait for any pending auto-save so the conversion
                          // reads the latest sub-task fields instead of a
                          // pre-edit snapshot.
                          if (!(await flushPendingSave())) return;
                          props.onConvertSubtaskToTask?.(activeSubtask.id);
                        }}
                        aria-label="Convert sub-task to task"
                        className="text-warm-text-tertiary hover:text-warm-blue p-0.5 rounded flex-shrink-0"
                      >
                        <FileUp size={14} />
                      </button>
                    </Tooltip>
                  )}
                </div>
                <ChevronRight
                  size={14}
                  className="text-warm-text-tertiary flex-shrink-0"
                />
                {(() => {
                  const isSubtaskCompleted = !!activeSubtask.completed;
                  const showSubtaskHoverPreview =
                    hoveringSubtaskCompleteCheckbox && !isSubtaskCompleted;
                  return (
                    <button
                      type="button"
                      onClick={toggleActiveSubtaskCompletion}
                      onMouseEnter={() =>
                        setHoveringSubtaskCompleteCheckbox(true)
                      }
                      onMouseLeave={() =>
                        setHoveringSubtaskCompleteCheckbox(false)
                      }
                      onFocus={() => setHoveringSubtaskCompleteCheckbox(true)}
                      onBlur={() => setHoveringSubtaskCompleteCheckbox(false)}
                      aria-label={
                        isSubtaskCompleted
                          ? "Mark as incomplete"
                          : "Mark as complete"
                      }
                      title={
                        isSubtaskCompleted
                          ? "Mark as incomplete"
                          : "Mark as complete"
                      }
                      className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-150 ease-in-out ${
                        isSubtaskCompleted
                          ? "bg-warm-blue border-warm-blue hover:bg-warm-blue/90"
                          : showSubtaskHoverPreview
                            ? "bg-warm-blue/25 border-warm-blue scale-110 shadow-soft-sm"
                            : "border-warm-text-tertiary hover:border-warm-blue"
                      }`}
                    >
                      {isSubtaskCompleted && (
                        <Check size={14} className="text-white" />
                      )}
                      {showSubtaskHoverPreview && (
                        <Check
                          size={14}
                          strokeWidth={3}
                          className="text-warm-blue opacity-80"
                        />
                      )}
                    </button>
                  );
                })()}
                <h3
                  className={`font-heading text-base sm:text-xl truncate ${
                    activeSubtask.completed
                      ? "line-through text-warm-text-tertiary"
                      : "text-warm-text-primary"
                  } ${
                    hoveringSubtaskCompleteCheckbox && !activeSubtask.completed
                      ? "title-dissolve-active"
                      : ""
                  }`}
                >
                  {activeSubtask.completed
                    ? activeSubtask.title
                    : splitGraphemes(activeSubtask.title).map((ch, i, arr) => (
                        <span
                          key={i}
                          className="dissolve-char"
                          style={
                            {
                              "--char-delay": `${(arr.length - 1 - i) * 22}ms`,
                            } as React.CSSProperties
                          }
                        >
                          {ch === " " ? " " : ch}
                        </span>
                      ))}
                </h3>
              </div>
            ) : (
              <>
                {mode === "edit" &&
                  (() => {
                    const isCompleted = (task as Task).completed;
                    const showHoverPreview =
                      hoveringCompleteCheckbox && !isCompleted;
                    return (
                      <button
                        type="button"
                        onClick={toggleCompletion}
                        onMouseEnter={() =>
                          setHoveringCompleteCheckbox(true)
                        }
                        onMouseLeave={() =>
                          setHoveringCompleteCheckbox(false)
                        }
                        onFocus={() => setHoveringCompleteCheckbox(true)}
                        onBlur={() => setHoveringCompleteCheckbox(false)}
                        aria-label={
                          isCompleted
                            ? "Mark as incomplete"
                            : "Mark as complete"
                        }
                        className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-150 ease-in-out ${
                          isCompleted
                            ? "bg-warm-blue border-warm-blue hover:bg-warm-blue/90"
                            : showHoverPreview
                              ? "bg-warm-blue/25 border-warm-blue scale-110 shadow-soft-sm"
                              : "border-warm-text-tertiary hover:border-warm-blue"
                        }`}
                      >
                        {isCompleted && (
                          <Check size={14} className="text-white" />
                        )}
                        {showHoverPreview && (
                          <Check
                            size={14}
                            strokeWidth={3}
                            className="text-warm-blue opacity-80"
                          />
                        )}
                      </button>
                    );
                  })()}
                {(() => {
                  const isCompleted =
                    mode === "edit" ? (task as Task).completed : false;
                  const showTitleHoverPreview =
                    mode === "edit" &&
                    hoveringCompleteCheckbox &&
                    !isCompleted;
                  const titleText = task.title;
                  // Per-char overlay covers the input visually so the same
                  // staggered right-to-left dissolve as the board card plays
                  // when hovering the checkbox. The input keeps caret + edit
                  // behaviour underneath; clicks fall through the overlay.
                  return (
                    <div className="relative flex-1 min-w-0 flex items-center">
                      <input
                        type="text"
                        value={titleText}
                        onChange={(e) => handleTitleChange(e.target.value)}
                        className="w-full min-w-0 bg-transparent border-none focus:outline-none focus:ring-0 font-heading text-lg sm:text-xl text-transparent caret-warm-text-primary placeholder-warm-text-tertiary truncate px-0 py-1"
                        placeholder="Task title..."
                        autoFocus={mode === "add"}
                        aria-label="Task title"
                        dir="auto"
                      />
                      {titleText && (
                        <div
                          aria-hidden="true"
                          className={`pointer-events-none absolute inset-0 flex items-center font-heading text-lg sm:text-xl truncate px-0 py-1 ${
                            isCompleted
                              ? "line-through text-warm-text-tertiary"
                              : "text-warm-text-primary"
                          } ${showTitleHoverPreview ? "title-dissolve-active" : ""}`}
                        >
                          <span dir="auto" className="truncate">
                            {isCompleted
                              ? titleText
                              : splitGraphemes(titleText).map((ch, i, arr) => (
                                  <span
                                    key={i}
                                    className="dissolve-char"
                                    style={
                                      {
                                        "--char-delay": `${(arr.length - 1 - i) * 22}ms`,
                                      } as React.CSSProperties
                                    }
                                  >
                                    {ch === " " ? " " : ch}
                                  </span>
                                ))}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {mode === "edit" &&
              props.mode === "edit" &&
              props.onConvertToSubtask &&
              !activeSubtask &&
              ((task as Task).subtasks?.length ?? 0) === 0 &&
              !(task as Task).recurrence_frequency && (
                <Tooltip content="Convert to sub-task" placement="bottom">
                  <button
                    onClick={async () => {
                      if (!(await flushPendingSave())) return;
                      props.onConvertToSubtask?.((task as Task).id);
                    }}
                    aria-label="Convert task to sub-task"
                    className="text-warm-text-secondary hover:text-warm-blue transition-colors p-1.5 sm:p-2 rounded-soft hover:bg-warm-blue-light/20 dark:hover:bg-warm-blue/20"
                  >
                    <CornerDownRight size={20} />
                  </button>
                </Tooltip>
              )}
            {/* Hidden while a sub-task is open. Archiving is a task-level
                concept — sub-tasks have no `archived` field — so the only
                thing this button could act on is the parent, which is not
                what the user is looking at. Mirrors the trash icon's rule
                that the header only ever acts on the item being viewed. */}
            {mode === "edit" &&
              props.mode === "edit" &&
              props.onArchive &&
              !activeSubtask && (
                <Tooltip
                  content={
                    (task as Task).archived ? "Restore task" : "Archive task"
                  }
                  placement="bottom"
                >
                  <button
                    onClick={() => {
                      flushPendingSave();
                      props.onArchive?.((task as Task).id);
                    }}
                    aria-label={
                      (task as Task).archived ? "Restore task" : "Archive task"
                    }
                    className="text-warm-text-secondary hover:text-warm-blue transition-colors p-1.5 sm:p-2 rounded-soft hover:bg-warm-blue-light/20 dark:hover:bg-warm-blue/20"
                  >
                    {(task as Task).archived ? (
                      <ArchiveRestore size={20} />
                    ) : (
                      <Archive size={20} />
                    )}
                  </button>
                </Tooltip>
              )}
            {mode === "edit" &&
              props.mode === "edit" &&
              (activeSubtask ? props.onDeleteSubtask : sharedListSync.canDelete && props.onDelete) && (
                <button
                  onClick={() => {
                    flushPendingSave();
                    // Act on whatever is being viewed: the open sub-task when
                    // in the breadcrumb sub-view, otherwise the parent task.
                    if (activeSubtask) {
                      props.onDeleteSubtask?.(activeSubtask.id);
                    } else {
                      props.onDelete?.((task as Task).id);
                    }
                  }}
                  aria-label={activeSubtask ? "Delete sub-task" : "Delete task"}
                  className="text-warm-text-secondary hover:text-red-500 dark:hover:text-red-400 transition-colors p-1.5 sm:p-2 rounded-soft hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Trash2 size={20} />
                </button>
              )}
            {hasMultipleTasks && mode === "edit" && props.mode === "edit" && (
              <>
                <Tooltip
                  content={`Previous task (${formatShortcut("⌥A")})`}
                  placement="bottom"
                >
                  <button
                    onClick={() => {
                      flushPendingSave();
                      props.onNavigatePrev?.();
                    }}
                    aria-label="Previous task"
                    className="hidden sm:block text-warm-text-secondary hover:text-warm-text-primary transition-colors p-1.5 sm:p-2 rounded-soft hover:bg-warm-bg-elevated"
                  >
                    <ChevronLeft size={20} />
                  </button>
                </Tooltip>
                <Tooltip
                  content={`Next task (${formatShortcut("⌥D")})`}
                  placement="bottom"
                >
                  <button
                    onClick={() => {
                      flushPendingSave();
                      props.onNavigateNext?.();
                    }}
                    aria-label="Next task"
                    className="hidden sm:block text-warm-text-secondary hover:text-warm-text-primary transition-colors p-1.5 sm:p-2 rounded-soft hover:bg-warm-bg-elevated"
                  >
                    <ChevronRight size={20} />
                  </button>
                </Tooltip>
              </>
            )}
            <button
              onClick={() => {
                if (mode === "edit") flushPendingSave();
                onClose();
              }}
              aria-label="Close dialog"
              className="text-warm-text-secondary hover:text-warm-text-primary transition-colors p-1.5 sm:p-2"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="flex-1 overflow-y-auto">
          {activeSubtask && mode === "edit" && props.mode === "edit" ? (
            <SubtaskDetail
              subtask={activeSubtask}
              userId={userId}
              weekStartDay={weekStartDay}
              pickerAvailable={pickerAvailable}
              onChange={updateActiveSubtask}
              onOpenPicker={() => setPickerTarget("subtask")}
              onToggleTagPopover={toggleTagPopover}
              onBlockWorkHours={() => setShowWorkHoursBlock("subtask")}
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 p-3 sm:p-6 min-h-full">
              {/* LEFT COLUMN - Main Content (2/3 width) */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-6">

                {/* Description - View/Edit Mode */}
                <div className="flex-1 flex flex-col min-h-[200px]">
                  <label className="block text-sm font-medium text-warm-text-secondary mb-2">
                    Description
                  </label>
                  {!isDescriptionEditing && mode === "edit" ? (
                    <div
                      onClick={() => setIsDescriptionEditing(true)}
                      className="flex-1 bg-warm-bg-elevated border border-warm-border rounded-soft px-4 py-3 cursor-text hover:border-warm-blue/50 dark:hover:border-warm-blue/50 transition-colors min-h-[120px]"
                    >
                      {task.description ? (
                        <div
                          className="task-brief text-warm-text-primary"
                          dangerouslySetInnerHTML={{ __html: task.description }}
                        />
                      ) : (
                        <p className="text-warm-text-tertiary italic">
                          Click to add a description...
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col">
                      <RichTextEditor
                        content={task.description}
                        onChange={(content) => {
                          if (mode === "edit") {
                            onTaskChange({
                              ...task,
                              description: content,
                            } as Task);
                          } else {
                            onTaskChange({
                              ...task,
                              description: content,
                            } as NewTask);
                          }
                        }}
                        placeholder="Add a description..."
                        userId={userId}
                      />
                      {mode === "edit" && (
                        <button
                          onClick={() => setIsDescriptionEditing(false)}
                          className="mt-2 self-end px-3 py-1.5 bg-warm-blue text-white rounded-soft text-sm hover:bg-warm-blue-dark transition-colors"
                        >
                          Done
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Links Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-warm-text-secondary flex items-center gap-2">
                      <Link size={16} />
                      Links
                      {task.links && task.links.length > 0 && (
                        <span className="text-xs text-warm-text-tertiary font-normal">
                          ({task.links.length})
                        </span>
                      )}
                    </div>
                    {!showLinkForm && (
                      <button
                        type="button"
                        onClick={() => setShowLinkForm(true)}
                        aria-label="Add link"
                        className="p-1 rounded-soft text-warm-text-secondary hover:text-warm-text-primary hover:bg-warm-bg-elevated transition-all duration-150 ease-in-out"
                      >
                        <Plus size={16} />
                      </button>
                    )}
                  </div>

                  {task.links && task.links.length > 0 && (
                    <LinkList
                      links={
                        mode === "edit"
                          ? (task as Task).links || []
                          : ((task as NewTask).links || []).map((l, i) => ({
                              ...l,
                              id: String(i),
                              task_id: "",
                              created_at: "",
                            }))
                      }
                      onEdit={(linkId) => {
                        handleEditLink(linkId);
                      }}
                      onDelete={(linkId) => {
                        if (mode === "edit") {
                          handleDeleteLink(linkId);
                        } else {
                          // For add mode, linkId is the temporary index (String(i))
                          const index = parseInt(linkId, 10);
                          if (!isNaN(index)) {
                            handleDeleteLink(index);
                          }
                        }
                      }}
                      className="mb-2"
                    />
                  )}

                  {showLinkForm && (
                    <LinkForm
                      key={editingLinkId ?? "new"}
                      initialUrl={editingLink?.url}
                      initialDescription={editingLink?.description}
                      submitLabel={editingLinkId ? "Save Link" : "Add Link"}
                      cancelAriaLabel={
                        editingLinkId
                          ? "Cancel editing link"
                          : "Cancel adding link"
                      }
                      onSubmit={handleAddLink}
                      onCancel={() => {
                        setShowLinkForm(false);
                        setEditingLinkId(null);
                      }}
                    />
                  )}
                </div>

                {/* Attachments Section */}
                {mode === "edit" ? (
                  <TaskAttachmentsSection
                    mode="edit"
                    attachments={(task as Task).attachments || []}
                    onUpload={handleUploadAttachment}
                    onDelete={handleDeleteAttachment}
                    onRename={
                      props.mode === "edit" ? handleRenameAttachment : undefined
                    }
                    isDeleting={deletingAttachmentId}
                  />
                ) : (
                  <TaskAttachmentsSection
                    mode="add"
                    pendingFiles={(task as NewTask).pending_files || []}
                    onUpload={handleUploadAttachment}
                    onRemovePendingFile={handleRemovePendingAttachment}
                  />
                )}

                {/* Subtasks Section */}
                {mode === "edit" ? (
                  <SubtaskList
                    mode="edit"
                    taskId={(task as Task).id}
                    subtasks={(task as Task).subtasks || []}
                    onSubtasksChange={(next) =>
                      onTaskChange({ ...task, subtasks: next } as Task)
                    }
                    onNavigateToSubtask={setActiveSubtaskId}
                  />
                ) : (
                  <SubtaskList
                    mode="add"
                    subtasks={(task as NewTask).subtasks || []}
                    onSubtasksChange={(next) =>
                      onTaskChange({ ...task, subtasks: next } as NewTask)
                    }
                  />
                )}
              </div>

              {/* RIGHT COLUMN - Metadata (1/3 width) */}
              <div className="space-y-4 sm:space-y-5">
                {/* List */}
                <ListPicker
                  lists={lists}
                  selectedListId={task.list_id}
                  isOpen={showListPopover}
                  onToggle={() => setShowListPopover((v) => !v)}
                  onClose={() => setShowListPopover(false)}
                  onSelectList={sharedListSync.onSelectList}
                  onClearList={sharedListSync.onClearList}
                  onAddList={() => setShowAddListDialog(true)}
                  disabledReason={sharedListSync.disabledReason}
                />
                {sharedListSync.addedByNode}
                {sharedListSync.confirmDialogNode}

                {/* Priority */}
                <TaskPriorityButtons
                  priority={task.priority}
                  onChange={(priority) => {
                    if (mode === "edit") {
                      onTaskChange({ ...task, priority } as Task);
                    } else {
                      onTaskChange({ ...task, priority } as NewTask);
                    }
                  }}
                />

                {/* Tags */}
                <TagPicker
                  selectedTags={tags.filter((tag) =>
                    selectedTagIds.includes(tag.id),
                  )}
                  onToggleTagPopover={toggleTagPopover}
                />

                {/* Schedule (hidden when recurrence is on — date/time/duration move
                    into the recurrence section in that case). */}
                {!task.recurrence_frequency && (
                <div>
                  <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
                    Schedule
                    <Tooltip content="When you plan to work on this task" multiline>
                      <span
                        tabIndex={0}
                        role="button"
                        aria-label="When you plan to work on this task"
                        className="text-warm-text-tertiary cursor-help ml-1 text-xs"
                      >
                        ℹ️
                      </span>
                    </Tooltip>
                  </label>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-stretch gap-2">
                      <div className="relative flex-1">
                        <ScheduledDateField
                          value={task.scheduled_date || null}
                          onChange={(newDate) => {
                            const cleared = newDate
                              ? {}
                              : { scheduled_time: null, duration: null, persistent: false, recurrence_frequency: null, recurrence_interval: 1, recurrence_day_of_week: null, recurrence_end_date: null };
                            if (mode === "edit") {
                              onTaskChange({
                                ...task,
                                scheduled_date: newDate,
                                ...cleared,
                              } as Task);
                            } else {
                              onTaskChange({
                                ...task,
                                scheduled_date: newDate,
                                ...cleared,
                              } as NewTask);
                            }
                          }}
                          restrictToWorkingHours={workHoursActive}
                          weekStartDay={weekStartDay}
                        />
                      </div>
                      {pickerAvailable && (
                        <Tooltip content="Pick on calendar">
                          <button
                            type="button"
                            aria-label="Pick on calendar"
                            onClick={() => setPickerTarget("task")}
                            className="hidden md:inline-flex items-center justify-center px-3 bg-warm-bg-elevated border border-warm-border rounded-soft text-warm-text-secondary hover:border-warm-blue/50 dark:hover:border-warm-blue/50 transition-all duration-150 ease-in-out"
                          >
                            <CalendarRange size={16} />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                    {task.scheduled_date && renderTimeAndDurationFields()}
                  </div>
                </div>
                )}

                {/* Recurring Task — the toggle and every control under it save
                    like any other field, on the longer scheduling window. */}
                <RecurrenceConfig
                  task={task}
                  mode={mode}
                  onSchedulingChange={updateScheduling}
                  persistentActive={persistentActive}
                  workHoursActive={workHoursActive}
                  timeAndDurationFields={renderTimeAndDurationFields()}
                  onFlushPendingSave={flushPendingSave}
                  onClose={onClose}
                />

                {/* Carry over (hidden when recurrence is on — the two are
                    mutually exclusive). */}
                {task.scheduled_date && !task.recurrence_frequency && (
                  <div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={persistentActive}
                      onClick={() => {
                        const updates = {
                          ...task,
                          persistent: !persistentActive,
                        };
                        if (mode === "edit") {
                          onTaskChange(updates as Task);
                        } else {
                          onTaskChange(updates as NewTask);
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-soft text-sm transition-all duration-150 ease-in-out text-left ${
                        persistentActive
                          ? "bg-warm-blue/10 border border-warm-blue/30"
                          : "bg-warm-bg-elevated border border-warm-border hover:border-warm-blue/50 dark:hover:border-warm-blue/50"
                      }`}
                    >
                      <CalendarClock
                        size={16}
                        className={
                          persistentActive
                            ? "text-warm-blue flex-shrink-0"
                            : "text-warm-text-tertiary flex-shrink-0"
                        }
                      />
                      <div className="flex-1 min-w-0">
                        <div
                          className={
                            persistentActive
                              ? "font-medium text-warm-blue"
                              : "text-warm-text-secondary"
                          }
                        >
                          Carry over
                        </div>
                        <div className="text-[11px] text-warm-text-tertiary leading-tight mt-0.5">
                          Moves to today each day until it is done. The time stays the same.
                        </div>
                      </div>
                      <div
                        className={`relative w-9 h-5 rounded-full transition-colors duration-150 ease-in-out flex-shrink-0 ${
                          persistentActive
                            ? "bg-warm-blue"
                            : "bg-warm-border"
                        }`}
                      >
                        <div
                          className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-150 ease-in-out ${
                            persistentActive
                              ? "translate-x-4"
                              : "translate-x-0"
                          }`}
                        />
                      </div>
                    </button>
                  </div>
                )}

                {/* Working hours only (hidden when recurrence is on — mutually
                    exclusive with Repeat, combines with Carry over). */}
                {!task.recurrence_frequency && (
                  <div>
                    <WorkingHoursToggle
                      active={workHoursActive}
                      onClick={() => {
                        const turningOn = !workHoursActive;
                        if (
                          turningOn &&
                          !isValidWorkHoursSlot(
                            task.scheduled_date,
                            task.scheduled_time,
                          )
                        ) {
                          setShowWorkHoursBlock("task");
                          return;
                        }
                        const updates = {
                          ...task,
                          working_hours_only: turningOn,
                        };
                        if (mode === "edit") {
                          onTaskChange(updates as Task);
                        } else {
                          onTaskChange(updates as NewTask);
                        }
                      }}
                    />
                  </div>
                )}

                {/* Deadline */}
                <div>
                  <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
                    Deadline
                    <Tooltip content="The final deadline for this task" multiline>
                      <span
                        tabIndex={0}
                        role="button"
                        aria-label="The final deadline for this task"
                        className="text-warm-text-tertiary cursor-help ml-1 text-xs"
                      >
                        ℹ️
                      </span>
                    </Tooltip>
                  </label>
                  <div className="relative">
                    <Calendar
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-text-tertiary"
                      size={16}
                    />
                    <input
                      type="date"
                      aria-label="Deadline date"
                      value={task.deadline_date || ""}
                      onChange={(e) => {
                        if (mode === "edit") {
                          onTaskChange({
                            ...task,
                            deadline_date: e.target.value || null,
                          } as Task);
                        } else {
                          onTaskChange({
                            ...task,
                            deadline_date: e.target.value || null,
                          } as NewTask);
                        }
                      }}
                      className="w-full bg-warm-bg-elevated border border-warm-border rounded-soft pl-10 pr-3 py-2 text-sm text-warm-text-primary focus:outline-none focus:ring-2 focus:ring-warm-blue"
                    />
                  </div>
                </div>

                {/* Created Date (Edit mode only) */}
                {mode === "edit" && (task as Task).created_at && (
                  <div>
                    <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
                      Created
                    </label>
                    <div className="bg-warm-bg-elevated border border-warm-border rounded-soft px-3 py-2 text-xs text-warm-text-secondary">
                      {new Date((task as Task).created_at!).toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        },
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer - Save button for Add mode */}
        {mode === "add" && (
          <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-warm-border flex justify-end gap-2 sm:gap-3 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-warm-text-secondary hover:text-warm-text-primary transition-colors"
            >
              Discard
            </button>
            <button
              onClick={() => onSave()}
              disabled={!task.title.trim()}
              className="px-6 py-2 bg-warm-blue hover:bg-warm-blue-dark disabled:bg-warm-bg-elevated disabled:cursor-not-allowed text-white rounded-soft transition-colors shadow-soft-sm font-medium"
            >
              Add Task
            </button>
          </div>
        )}
      </div>

      {/* Tag Popover */}
      <AnimatePresence popover>
        {showTagPopover && (
          <TagPopover
            tags={tags}
            selectedTagIds={
              activeSubtask
                ? activeSubtask.tags?.map((t) => t.id) || []
                : selectedTagIds
            }
            onToggleTag={activeSubtask ? toggleSubtaskTag : toggleTag}
            onClose={() => setShowTagPopover(false)}
            anchorEl={tagPopoverAnchor}
          />
        )}
      </AnimatePresence>

      {/* Add List Dialog */}
      <AnimatePresence>
        {showAddListDialog && (
          <AddListDialog
            newListName={newListName}
            setNewListName={setNewListName}
            newListEmoji={newListEmoji}
            setNewListEmoji={setNewListEmoji}
            onClose={() => setShowAddListDialog(false)}
            onAdd={handleAddList}
          />
        )}
      </AnimatePresence>

      {/* Move Subtask Picker */}
      {showMoveSubtaskPicker &&
        activeSubtask &&
        props.mode === "edit" &&
        props.onMoveSubtask && (
          <TaskPickerDialog
            tasks={props.allTasksForMove || []}
            lists={lists}
            excludeTaskIds={[(task as Task).id]}
            onClose={() => setShowMoveSubtaskPicker(false)}
            onSelect={(selection) => {
              flushPendingSave();
              setShowMoveSubtaskPicker(false);
              props.mode === "edit" &&
                props.onMoveSubtask?.(activeSubtask.id, selection.task.id);
            }}
          />
        )}

      <AnimatePresence>
        {showCompleteConfirm && mode === "edit" && (
          <CompleteWithSubtasksDialog
            taskTitle={(task as Task).title}
            incompleteSubtasks={
              (task as Task).subtasks?.filter((s) => !s.completed) ?? []
            }
            onCancel={() => setShowCompleteConfirm(false)}
            onConfirm={confirmCompleteWithSubtasks}
          />
        )}
      </AnimatePresence>

      {/* Schedule picker — modal-on-modal layout (option A) */}
      <AnimatePresence>
        {pickerTarget && pickerInitial && pickerAvailable && (
          <FullWindowLayer
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-40 p-2 sm:p-6 animate-dialog-backdrop"
            onBackdropClick={() => setPickerTarget(null)}
          >
            <TrappedPanel
              aria-label="Pick on calendar"
              className="bg-warm-bg-card rounded-card border border-warm-border shadow-soft-lg w-full max-w-[67rem] h-[85vh] flex flex-col animate-dialog-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <SchedulePickerCalendar
                targetKey={pickerInitial.targetKey}
                targetTitle={pickerInitial.targetTitle}
                targetPriority={pickerInitial.targetPriority}
                initialDate={pickerInitial.initialDate}
                initialTime={pickerInitial.initialTime}
                initialDuration={pickerInitial.initialDuration}
                tasks={allTasksForPicker || []}
                calendarEvents={calendarEventsForPicker || []}
                onFetchEvents={
                  onFetchCalendarEvents || (async () => {})
                }
                isLoadingEvents={!!isLoadingCalendarEvents}
                weekStartDay={weekStartDay}
                defaultDuration={defaultTaskDuration}
                onApply={(date, time, duration) =>
                  applySchedulePick(pickerTarget, date, time, duration)
                }
                onCancel={() => setPickerTarget(null)}
                onClear={() => clearSchedulePick(pickerTarget)}
                restrictToWorkingHours={pickerInitial.restrictToWorkingHours}
              />
            </TrappedPanel>
          </FullWindowLayer>
        )}
      </AnimatePresence>

      {/* Working hours block — shown when the user toggles "Working hours only"
          ON for a task or subtask whose current slot falls outside the window.
          We block rather than silently snap so the user retains control over
          where the task lands. */}
      <AnimatePresence>
        {showWorkHoursBlock !== null && (
          <FullWindowLayer
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-dialog-backdrop"
            onBackdropClick={() => setShowWorkHoursBlock(null)}
          >
            <TrappedPanel
              aria-labelledby="work-hours-block-title"
              className="bg-warm-bg-card rounded-card border border-warm-border shadow-soft-lg w-full max-w-md p-6 animate-dialog-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-warm-blue/10 flex items-center justify-center flex-shrink-0">
                  <Briefcase size={20} className="text-warm-blue" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    id="work-hours-block-title"
                    className="font-heading text-lg text-warm-text-primary"
                  >
                    Working hours only
                  </h3>
                  <p className="text-sm text-warm-text-secondary mt-1 leading-relaxed">
                    This{" "}
                    {showWorkHoursBlock === "subtask" ? "subtask" : "task"} is
                    currently scheduled outside working hours. Move it to a
                    Mon–Fri slot between 9 AM and 5 PM, then turn this on.
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowWorkHoursBlock(null)}
                  className="px-4 py-2 rounded-soft text-sm font-medium bg-warm-blue text-white hover:bg-warm-blue/90 transition-all duration-150 ease-in-out"
                >
                  Got it
                </button>
              </div>
            </TrappedPanel>
          </FullWindowLayer>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TaskDialog;
